import { useQuery } from "@tanstack/react-query";
import { rawFetch } from "@/lib/api/client";

type EligiblePack = { id: string; name: string; semver: string; kind: string; digest: string; universal: boolean };

export function EligiblePackSelector({ workspace, industry, selected, onChange, disabled }: {
  workspace: string | null; industry: string; selected: string[]; onChange: (ids: string[]) => void; disabled: boolean;
}) {
  const query = useQuery<{ results: EligiblePack[] }>({
    queryKey: ["eligible-ontology-packs", workspace, industry],
    queryFn: () => rawFetch(`/ontology/eligible-packs/?workspace=${encodeURIComponent(workspace!)}&industry=${encodeURIComponent(industry)}`),
    enabled: Boolean(workspace),
  });
  return <section className="rounded border border-border bg-bg/60 p-3" aria-label="Eligible ontology packs">
    <h4 className="text-xs font-semibold uppercase tracking-wide text-text">Eligible signed packs</h4>
    <p className="mt-1 text-xs text-muted">Showing universal core and packs for {industry.replace(/_/g, " ")}. Exact versions are pinned. Synthetic starters are examples, not certified standards; external standards use a separate rights workflow.</p>
    {query.isLoading && <p className="mt-2 text-xs text-muted">Loading packs…</p>}
    {query.isError && <p role="alert" className="mt-2 text-xs text-bad">Could not load eligible packs.</p>}
    <div className="mt-2 max-h-52 space-y-1 overflow-y-auto">
      {query.data?.results.map((pack) => <label key={pack.id} className="flex items-start gap-2 rounded border border-border p-2 text-xs text-text">
        <input type="checkbox" checked={selected.includes(pack.id)} disabled={disabled || (!selected.includes(pack.id) && selected.length >= 10)}
          onChange={() => onChange(selected.includes(pack.id) ? selected.filter((id) => id !== pack.id) : [...selected, pack.id])} />
        <span><strong>{pack.name}</strong> · {pack.semver} · {pack.universal ? "Universal core" : "Industry starter/overlay"}
          <span className="block font-mono text-[10px] text-muted" title={pack.digest}>Digest {pack.digest.slice(0, 12)}…</span>
        </span>
      </label>)}
      {query.data?.results.length === 0 && <p className="text-xs text-muted">No eligible signed packs are available.</p>}
      {query.data && industry !== "general" && !query.data.results.some((pack) => !pack.universal) &&
        <p className="text-xs text-warn">No authored {industry.replace(/_/g, " ")} overlay is available yet. Universal core packs can still start a reviewed draft.</p>}
    </div>
  </section>;
}
